import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "previews", "template", "count", "error", "removal", "saved"]
  static values = { existing: Number, limit: Number, tooMany: String, invalid: String }

  connect() {
    this.files = Array.from(this.inputTarget.files)
    this.urls = []
    this.render()
  }

  disconnect() { this.releaseUrls() }

  get savedCount() {
    return this.existingValue - this.removalTargets.filter(input => input.checked).length
  }

  select() {
    const selected = Array.from(this.inputTarget.files)
    let message = ""
    for (const file of selected) {
      if (file.size > 15 * 1024 * 1024 || !(/^(image\/(jpeg|png|webp|heic|heif))$/.test(file.type) || (!file.type && /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)))) {
        message = this.invalidValue
        continue
      }
      if (this.files.some(existing => existing.name === file.name && existing.size === file.size && existing.lastModified === file.lastModified)) continue
      if (this.savedCount + this.files.length >= this.limitValue) {
        message = this.tooManyValue
        continue
      }
      this.files.push(file)
    }
    this.sync()
    this.errorTarget.textContent = message
  }

  remove(event) {
    this.files.splice(Number(event.currentTarget.dataset.index), 1)
    this.sync()
    this.errorTarget.textContent = ""
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.inputTarget.focus()
  }

  removeSaved(event) {
    const photo = this.savedTargets.find(element => element.contains(event.currentTarget))
    const removal = this.removalTargets.find(input => photo.contains(input))
    removal.checked = true
    photo.hidden = true
    this.updateCount()
    removal.dispatchEvent(new Event("change", { bubbles: true }))
    this.inputTarget.focus()
  }

  fileKey(file) { return `${file.name}:${file.size}:${file.lastModified}` }

  uploading(event) {
    if (event.target !== this.element.closest("form")) return
    this.inFlight = event.detail.files.map(file => this.fileKey(file))
    this.render()
  }

  uploaded(event) {
    if (event.target !== this.element.closest("form")) return
    const uploadedKeys = event.detail.files.map(file => this.fileKey(file))
    this.files = this.files.filter(file => !uploadedKeys.includes(this.fileKey(file)))
    event.detail.removals.forEach(id => {
      const input = this.removalTargets.find(input => input.value === id)
      const photo = this.savedTargets.find(photo => photo.contains(input))
      photo?.remove()
      this.existingValue -= 1
    })
    // This HTML is rendered by the authenticated assessment endpoint.
    const template = document.createElement("template")
    template.innerHTML = event.detail.html
    this.existingValue += template.content.querySelectorAll(".icomos-saved-photo").length
    this.element.append(template.content)
    this.sync()
  }

  finished(event) {
    if (event.target !== this.element.closest("form")) return
    this.inFlight = []
    this.render()
  }

  sync() {
    const transfer = new DataTransfer()
    this.files.forEach(file => transfer.items.add(file))
    this.inputTarget.files = transfer.files
    this.render()
  }

  updateCount() {
    const count = this.savedCount + this.files.length
    this.countTarget.textContent = `${count} / ${this.limitValue}`
    if (count > this.limitValue) this.errorTarget.textContent = this.tooManyValue
  }

  render() {
    this.releaseUrls()
    this.previewsTarget.replaceChildren()
    this.files.forEach((file, index) => {
      const preview = this.templateTarget.content.cloneNode(true)
      const image = preview.querySelector("img")
      const fallback = preview.querySelector(".icomos-photo-fallback")
      const url = URL.createObjectURL(file)
      this.urls.push(url)
      image.addEventListener("error", () => { image.hidden = true; fallback.hidden = false }, { once: true })
      image.src = url
      preview.querySelector("figcaption").textContent = file.name
      preview.querySelector("button").dataset.index = index
      preview.querySelector("button").disabled = this.inFlight?.includes(this.fileKey(file)) || false
      this.previewsTarget.append(preview)
    })
    this.updateCount()
  }

  releaseUrls() {
    this.urls.forEach(url => URL.revokeObjectURL(url))
    this.urls = []
  }
}
